import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { surveyService, establishmentService, referenceService } from '../../../services/surveyService';
import { Button, Input, Select, Card, Spinner, PageHeader } from '../../../shared/sharedComponent';
import DateTimePicker from '../../../shared/DateTimePicker';
import { toast } from 'react-toastify';
import './CreateSurvey.css';

const FORM_CONFIGS = {
  MQ1: {
    title: 'Survey of Manufacturing',
    survey_type: 'quarterly',
    sector: 'manufacturing',
    description: 'Covers Employment, Current & Capital Expenditure, and Production data.',
  },
  WRTQ1: {
    title: 'Survey of Wholesale & Retail Trade',
    survey_type: 'quarterly',
    sector: 'wholesale_retail',
    description: 'Covers Employment, Current & Capital Expenditure, and Revenue data.',
  },
  MM1: {
    title: 'Survey of Manufacturing',
    survey_type: 'monthly',
    sector: 'manufacturing',
    description: 'Covers Production and Export data for the month.',
  },
  Monthly_WRT: {
    title: 'Survey of Wholesale & Retail Trade',
    survey_type: 'monthly',
    sector: 'wholesale_retail',
    description: 'Covers Turnover / Revenue for the month.',
  },
  GQ1: {
    title: 'Generic Questionnaire',
    survey_type: 'quarterly',
    sector: 'all',
    description: 'A general questionnaire covering Employment and Sales Value across all sectors.',
  },
};

// Chronological comparator for "Q<n> <year>" strings — plain .sort() compares
// lexicographically (quarter digit before year), so "Q4 2025" sorts after
// "Q1 2026" even though it's chronologically earlier.
const compareQuarterNames = (a, b) => {
  const [qa, ya] = a.split(' ');
  const [qb, yb] = b.split(' ');
  if (ya !== yb) return parseInt(ya) - parseInt(yb);
  return parseInt(qa.slice(1)) - parseInt(qb.slice(1));
};

const MONTH_ORDER = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// Chronological comparator for "<Month> <year>" strings.
const compareMonthNames = (a, b) => {
  const [ma, ya] = a.split(' ');
  const [mb, yb] = b.split(' ');
  if (ya !== yb) return parseInt(ya) - parseInt(yb);
  return MONTH_ORDER.indexOf(ma) - MONTH_ORDER.indexOf(mb);
};

const STEPS = [
  { id: 1, label: 'Survey Type',     icon: '📋' },
  { id: 2, label: 'Period',          icon: '📅' },
  { id: 3, label: 'Data Sources',  icon: '🏢' },
  { id: 4, label: 'Dates',           icon: '⏰' },
  { id: 5, label: 'Review & Create', icon: '✅' },
];

const CreateSurvey = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const userSector = user?.sector_type || null; // null = admin (sees all)

  const [step, setStep]             = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Step 1
  const [selectedForm, setSelectedForm] = useState(searchParams.get('form') || '');

  // Step 2 — period
  const [allQuarters, setAllQuarters]           = useState([]);
  const [allMonths, setAllMonths]               = useState([]);
  const [selectedQuarterIds, setSelectedQuarterIds] = useState([]);
  const [selectedMonthIds, setSelectedMonthIds] = useState([]);
  const [quarterSearch, setQuarterSearch]       = useState('');

  // Universal survey toggle
  const [isUniversal, setIsUniversal] = useState(!!userSector); // ON by default for sector users
  const [autoDispatch, setAutoDispatch] = useState(false);

  // Sync isUniversal when user context loads (e.g. on refresh)
  useEffect(() => {
    if (userSector) setIsUniversal(true);
  }, [userSector]);

  // Step 3 — establishments
  const [establishments, setEstablishments] = useState([]);
  const [estLoading, setEstLoading]         = useState(false);
  const [selectedEstIds, setSelectedEstIds] = useState([]);
  const [estSearch, setEstSearch]           = useState('');

  // Step 4 — dates
  const [openDate, setOpenDate]     = useState('');
  const [closeDate, setCloseDate]   = useState('');
  const [surveyTitle, setSurveyTitle] = useState('');

  const cfg       = FORM_CONFIGS[selectedForm];
  const isMonthly = cfg?.survey_type === 'monthly';

  const getCurrentQuarter = () => Math.ceil((new Date().getMonth() + 1) / 3);

  const getAllowedQuarterNames = () => {
    const now  = new Date();
    const year = now.getFullYear();
    const curQ = getCurrentQuarter();
    const allowed = new Set();
    for (let i = 0; i < 4; i++) {
      let q = curQ - i;
      let y = year;
      while (q <= 0) { q += 4; y -= 1; }
      allowed.add(`Q${q} ${y}`);
    }
    return allowed;
  };

  const getFilteredMonths = (allMonthsData) => {
    const MONTH_NAMES = ['January','February','March','April','May','June',
                         'July','August','September','October','November','December'];
    const now      = new Date();
    const curMonth = now.getMonth(); // 0-indexed
    const curYear  = now.getFullYear();

    // Build allowed set: current month + last 3 months
    const allowed = new Set();
    for (let i = 0; i <= 3; i++) {
      let mIdx = curMonth - i;
      let year = curYear;
      if (mIdx < 0) { mIdx += 12; year -= 1; }
      allowed.add(`${MONTH_NAMES[mIdx]} ${year}`);
    }

    return allMonthsData
      .filter((m) => allowed.has(m.month_name))
      .sort((a, b) => {
        const [aN, aY] = [a.month_name.split(' ')[0], parseInt(a.month_name.split(' ')[1])];
        const [bN, bY] = [b.month_name.split(' ')[0], parseInt(b.month_name.split(' ')[1])];
        if (aY !== bY) return aY - bY;
        return MONTH_NAMES.indexOf(aN) - MONTH_NAMES.indexOf(bN);
      });
  };

  useEffect(() => {
    const allowedNames = getAllowedQuarterNames();
    referenceService.getQuarters().then((r) => {
      const seen = new Set();
      const deduped = r.data.data.filter((q) => {
        if (!allowedNames.has(q.quarter_name)) return false;
        if (seen.has(q.quarter_name)) return false;
        seen.add(q.quarter_name);
        return true;
      });
      const sorted = [...deduped].sort((a, b) => {
        const [qa, ya] = a.quarter_name.split(' ');
        const [qb, yb] = b.quarter_name.split(' ');
        if (ya !== yb) return parseInt(ya) - parseInt(yb);
        return parseInt(qa.slice(1)) - parseInt(qb.slice(1));
      });
      setAllQuarters(sorted);
    });
    referenceService.getMonths().then((r) => {
      setAllMonths(getFilteredMonths(r.data.data));
    });
  }, []);

  useEffect(() => {
    if (!selectedForm) return;
    const cfg = FORM_CONFIGS[selectedForm];
    if (isMonthly && selectedMonthIds.length) {
      const allNames = selectedMonthIds
        .map((id) => allMonths.find((m) => m.month_id === id)?.month_name)
        .filter(Boolean).sort(compareMonthNames);
      // Only show the last (most recent) month in the title
      const lastName = allNames[allNames.length - 1];
      setSurveyTitle(`${cfg.title} - ${lastName}`);
    } else if (!isMonthly && selectedQuarterIds.length) {
      const allNames = selectedQuarterIds
        .map((id) => allQuarters.find((q) => q.quarter_id === id)?.quarter_name)
        .filter(Boolean).sort(compareQuarterNames);
      // Only show the last (most recent) quarter in the title
      const lastName = allNames[allNames.length - 1];
      setSurveyTitle(`${cfg.title} - ${lastName}`);
    }
  }, [selectedForm, selectedQuarterIds, selectedMonthIds, allQuarters, allMonths, isMonthly]);

  useEffect(() => {
    if (!selectedForm) return;
    setEstLoading(true);
    const sectorParam   = cfg.sector === 'all' ? undefined : cfg.sector;
    const allSectors    = cfg.sector === 'all';
    establishmentService.getAll({ sector: sectorParam, search: estSearch, all_sectors: allSectors || undefined })
      .then((r) => setEstablishments(r.data.data))
      .catch(console.error)
      .finally(() => setEstLoading(false));
  }, [selectedForm, estSearch]);

  const MAX_QUARTERS = 4;
  const toggleQuarter = (id) => {
    setSelectedQuarterIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_QUARTERS) return prev;
      return [...prev, id];
    });
  };

  const toggleEst = (id) =>
    setSelectedEstIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleAll = () =>
    setSelectedEstIds(
      selectedEstIds.length === establishments.length
        ? []
        : establishments.map((e) => e.establishment_id)
    );

  const filteredQuarters = allQuarters.filter((q) =>
    q.quarter_name.toLowerCase().includes(quarterSearch.toLowerCase())
  );

  const canProceed = () => {
    if (step === 1) return !!selectedForm;
    if (step === 2) return isMonthly ? selectedMonthIds.length > 0 : selectedQuarterIds.length > 0;
    if (step === 3) return isUniversal || selectedEstIds.length > 0;
    if (step === 4) return !!openDate && !!closeDate && !!surveyTitle && new Date(closeDate) > new Date(openDate);
    return true;
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      await surveyService.create({
        survey_type:        cfg.survey_type,
        survey_sector_type: cfg.sector === 'all' ? (user?.sector_type || 'manufacturing') : cfg.sector,
        survey_form_code:   selectedForm,
        survey_title:       surveyTitle,
        survey_open_date:   openDate,
        survey_close_date:  closeDate,
        is_universal:       isUniversal,
        auto_dispatch:      isUniversal && autoDispatch,
        establishment_ids:  isUniversal ? [] : selectedEstIds,
        quarter_ids:        isMonthly ? [] : selectedQuarterIds,
        month_ids:          isMonthly ? selectedMonthIds : [],
        month_id:           isMonthly ? selectedMonthIds[0] || null : null,
      });
      toast.success(isUniversal
        ? 'Universal survey created successfully!'
        : selectedForm === 'GQ1'
          ? 'Survey created successfully!'
          : `${selectedEstIds.length} survey(s) created successfully!`);
      navigate('/internal/surveys');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create survey.');
    } finally {
      setSubmitting(false);
    }
  };

  const sectorLabel = cfg?.sector === 'manufacturing' ? 'Manufacturing'
    : cfg?.sector === 'wholesale_retail' ? 'Wholesale & Retail'
    : 'All Sectors';

  const renderStep = () => {
    switch (step) {

      // ── Step 1: Form type ─────────────────────────────────
      case 1:
        return (
          <div className="create-step animate-fade-in">
            <h2 className="create-step__title">Select Survey Form</h2>
            <p className="create-step__desc">Choose the type of survey you want to create and dispatch.</p>
            {userSector && (
              <div className="create-survey__sector-scope">
                <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/></svg>
                Showing <strong>{userSector.replace(/_/g, ' ').replace(/\w/g, c => c.toUpperCase())}</strong> surveys only — based on your assigned sector.
              </div>
            )}
            <div className="form-type-grid">
              {Object.entries(FORM_CONFIGS)
                .filter(([, config]) => !userSector || config.sector === userSector || config.sector === 'all')
                .map(([code, config]) => (
                <div
                  key={code}
                  className={`form-type-card ${selectedForm === code ? 'form-type-card--selected' : ''}`}
                  onClick={() => { setSelectedForm(code); setSelectedQuarterIds([]); setSelectedMonthIds([]); }}
                >
                  <div className="form-type-card__top">
                    <span className="form-type-card__code">{code}</span>
                    <span className={`form-type-card__freq form-type-card__freq--${config.survey_type}`}>
                      {config.survey_type}
                    </span>
                  </div>
                  <h4 className="form-type-card__title">{config.title}</h4>
                  <p className="form-type-card__desc">{config.description}</p>
                  <div className="form-type-card__sector">
                    <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4a2 2 0 012 2v1h1a1 1 0 011 1v7a1 1 0 01-1 1H3a1 1 0 01-1-1V6a1 1 0 011-1h1V4zm2 1h4V4H6v1z" clipRule="evenodd"/>
                    </svg>
                    {config.sector === 'manufacturing' ? 'Manufacturing'
                      : config.sector === 'wholesale_retail' ? 'Wholesale & Retail'
                      : 'All Sectors'}
                  </div>
                  {selectedForm === code && (
                    <div className="form-type-card__check">
                      <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* ── Universal Survey Toggle ── */}
            {selectedForm && (
              <div className="universal-toggle">
                <label className="universal-toggle__label">
                  <div className="universal-toggle__switch">
                    <input type="checkbox" checked={isUniversal}
                      onChange={(e) => {
                        setIsUniversal(e.target.checked);
                        if (e.target.checked) setSelectedEstIds([]);
                      }}
                    />
                    <span className="universal-toggle__slider" />
                  </div>
                  <div className="universal-toggle__text">
                    <span className="universal-toggle__title">Universal Survey</span>
                    <span className="universal-toggle__desc">
                      {isUniversal
                        ? 'Targets all active establishments in your sector. Toggle off to select specific ones.'
                        : 'Toggle on to target all establishments in your sector automatically.'
                      }
                    </span>
                  </div>
                </label>

                {isUniversal && (
                  <div className="universal-dispatch-options">
                    <p className="universal-dispatch-options__label">How would you like to dispatch this survey?</p>
                    <label className={`dispatch-option ${!autoDispatch ? 'dispatch-option--selected' : ''}`}>
                      <input type="radio" name="dispatchMode" checked={!autoDispatch}
                        onChange={() => setAutoDispatch(false)} />
                      <div className="dispatch-option__content">
                        <span className="dispatch-option__title">Manual Dispatch</span>
                        <span className="dispatch-option__desc">
                          Survey is created as a draft. You choose which providers to dispatch to later.
                        </span>
                      </div>
                    </label>
                    <label className={`dispatch-option ${autoDispatch ? 'dispatch-option--selected' : ''}`}>
                      <input type="radio" name="dispatchMode" checked={autoDispatch}
                        onChange={() => setAutoDispatch(true)} />
                      <div className="dispatch-option__content">
                        <span className="dispatch-option__title">Auto Dispatch</span>
                        <span className="dispatch-option__desc">
                          Automatically dispatch to all active providers in the sector immediately after creation.
                        </span>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      // ── Step 2: Period ────────────────────────────────────
      case 2:
        return (
          <div className="create-step animate-fade-in">
            <h2 className="create-step__title">Select Survey Period</h2>
            {isMonthly ? (
              <>
                <p className="create-step__desc">
                  Select one or more months this survey covers.{' '}
                  {(() => {
                    const m = new Date().getMonth() + 1;
                    const y = new Date().getFullYear();
                    if (m <= 2)  return `Showing months for ${y} and last 3 months of ${y - 1}.`;
                    if (m >= 11) return `Showing months for ${y} and first 3 months of ${y + 1}.`;
                    return `Showing all months for ${y}.`;
                  })()}
                </p>
                <div className="quarter-picker">
                  <div className="quarter-picker__toolbar">
                    <div className={`quarter-picker__selected-count`}>
                      {selectedMonthIds.length} selected
                    </div>
                  </div>
                  <div className="quarter-picker__grid">
                    {allMonths.length === 0 ? (
                      <p style={{ color: 'var(--gray-400)', padding: '24px', textAlign: 'center' }}>
                        No months available.
                      </p>
                    ) : allMonths.map((m) => {
                      const isSelected = selectedMonthIds.includes(m.month_id);
                      return (
                        <label key={m.month_id}
                          className={`quarter-chip ${isSelected ? 'quarter-chip--selected' : ''}`}>
                          <input type="checkbox" checked={isSelected}
                            onChange={() => setSelectedMonthIds((prev) =>
                              prev.includes(m.month_id)
                                ? prev.filter((x) => x !== m.month_id)
                                : [...prev, m.month_id]
                            )}
                          />
                          <span className="quarter-chip__name">{m.month_name}</span>
                          {isSelected && (
                            <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12" className="quarter-chip__check">
                              <path fillRule="evenodd" d="M13.707 4.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L6 10.586l6.293-6.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                            </svg>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  {selectedMonthIds.length > 0 && (
                    <div className="quarter-picker__summary">
                      <strong>Selected:</strong>{' '}
                      {selectedMonthIds
                        .map((id) => allMonths.find((m) => m.month_id === id)?.month_name)
                        .filter(Boolean).join(' · ')}
                      <button className="quarter-picker__clear" onClick={() => setSelectedMonthIds([])}>
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <p className="create-step__desc">
                  Select up to <strong>4 quarters</strong> this survey covers.
                  Showing current quarter and the 3 previous quarters.
                </p>
                <div className="quarter-picker">
                  <div className="quarter-picker__toolbar">
                    <Input
                      placeholder="Search quarters… e.g. Q1 2024"
                      value={quarterSearch}
                      onChange={(e) => setQuarterSearch(e.target.value)}
                      className="quarter-picker__search"
                    />
                    <div className={`quarter-picker__selected-count ${selectedQuarterIds.length >= MAX_QUARTERS ? 'quarter-picker__selected-count--max' : ''}`}>
                      {selectedQuarterIds.length}/{MAX_QUARTERS} selected
                    </div>
                  </div>
                  {selectedQuarterIds.length >= MAX_QUARTERS && (
                    <div className="quarter-picker__limit-notice">
                      Maximum of {MAX_QUARTERS} quarters reached. Deselect one to choose another.
                    </div>
                  )}
                  <div className="quarter-picker__grid">
                    {filteredQuarters.length === 0 ? (
                      <p style={{ color: 'var(--gray-400)', padding: '24px', textAlign: 'center' }}>
                        No quarters available for the current period.
                      </p>
                    ) : filteredQuarters.map((q) => {
                      const isSelected = selectedQuarterIds.includes(q.quarter_id);
                      const isDisabled = !isSelected && selectedQuarterIds.length >= MAX_QUARTERS;
                      return (
                        <label
                          key={q.quarter_id}
                          className={`quarter-chip ${isSelected ? 'quarter-chip--selected' : ''} ${isDisabled ? 'quarter-chip--disabled' : ''}`}
                        >
                          <input type="checkbox" checked={isSelected} disabled={isDisabled}
                            onChange={() => toggleQuarter(q.quarter_id)} />
                          <span className="quarter-chip__name">{q.quarter_name}</span>
                          {isSelected && (
                            <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12" className="quarter-chip__check">
                              <path fillRule="evenodd" d="M13.707 4.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L6 10.586l6.293-6.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                            </svg>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  {selectedQuarterIds.length > 0 && (
                    <div className="quarter-picker__summary">
                      <strong>Selected:</strong>{' '}
                      {selectedQuarterIds
                        .map((id) => allQuarters.find((q) => q.quarter_id === id)?.quarter_name)
                        .filter(Boolean).sort().join(' · ')}
                      <button className="quarter-picker__clear" onClick={() => setSelectedQuarterIds([])}>
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );

      // ── Step 3: Establishments ────────────────────────────
      case 3:
        return (
          <div className="create-step animate-fade-in">
            <h2 className="create-step__title">Select Establishments</h2>
            <p className="create-step__desc">
              {isUniversal
                ? <>This is a <strong>Universal Survey</strong> — it targets all active <strong>{sectorLabel}</strong> establishments. No selection needed.</>
                : <>Choose which establishments will receive this survey. Showing <strong>{sectorLabel}</strong> establishments.</>
              }
            </p>

            {isUniversal ? (
              <div className="universal-notice">
                <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                <span>All active establishments in the <strong>{sectorLabel}</strong> sector will receive this survey when dispatched.</span>
              </div>
            ) : (
              <>
                <div className="est-toolbar">
                  <Input
                    placeholder="Search establishments..."
                    value={estSearch}
                    onChange={(e) => setEstSearch(e.target.value)}
                    className="est-toolbar__search"
                  />
                  <label className="est-select-all">
                    <input
                      type="checkbox"
                      checked={selectedEstIds.length === establishments.length && establishments.length > 0}
                      onChange={toggleAll}
                    />
                    Select all ({establishments.length})
                  </label>
                </div>
                {estLoading ? (
                  <div className="est-loading"><Spinner /></div>
                ) : establishments.length === 0 ? (
                  <Card>
                    <div style={{ textAlign: 'center', padding: '32px', color: 'var(--gray-500)' }}>
                      No establishments found for this sector.
                    </div>
                  </Card>
                ) : (
                  <div className="est-list">
                    {establishments.map((e) => (
                      <label
                        key={e.establishment_id}
                        className={`est-item ${selectedEstIds.includes(e.establishment_id) ? 'est-item--selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedEstIds.includes(e.establishment_id)}
                          onChange={() => toggleEst(e.establishment_id)}
                          className="est-item__check"
                        />
                        <div className="est-item__body">
                          <span className="est-item__name">{e.establishment_name}</span>
                          <span className="est-item__email">{e.establishment_email}</span>
                        </div>
                        <div className="est-item__meta">
                          <span>{e.provider_count ?? 0} providers</span>
                          <span>{e.survey_count ?? 0} surveys</span>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                {selectedEstIds.length > 0 && (
                  <p className="create-step__selection-note">
                    ✅ {selectedEstIds.length} establishment(s) selected — {selectedEstIds.length} survey(s) will be created.
                  </p>
                )}
              </>
            )}
          </div>
        );

      // ── Step 4: Dates ─────────────────────────────────────
      case 4:
        return (
          <div className="create-step animate-fade-in">
            <h2 className="create-step__title">Set Dates & Title</h2>
            <p className="create-step__desc">Configure the survey window and confirm the title.</p>
            <div className="dates-form">
              <Input
                label="Survey Title"
                required
                value={surveyTitle}
                onChange={(e) => setSurveyTitle(e.target.value)}
                placeholder="Auto-generated — edit if needed"
              />
              <div className="dates-row">
                <DateTimePicker label="Open Date" required value={openDate} onChange={setOpenDate} />
                <DateTimePicker
                  label="Close Date" required value={closeDate} min={openDate} onChange={setCloseDate}
                  error={closeDate && openDate && new Date(closeDate) <= new Date(openDate)
                    ? 'Close date must be after open date' : undefined}
                />
              </div>
            </div>
          </div>
        );

      // ── Step 5: Review ────────────────────────────────────
      case 5:
        return (
          <div className="create-step animate-fade-in">
            <h2 className="create-step__title">Review & Create</h2>
            <p className="create-step__desc">Confirm the details below before creating the survey(s).</p>
            <div className="review-grid">
              <div className="review-section">
                <h4>Survey Details</h4>
                <div className="review-table">
                  {[
                    ['Form Code', selectedForm],
                    ['Type',      cfg?.survey_type],
                    ['Sector',    sectorLabel],
                    ['Title',     surveyTitle],
                    isMonthly
                      ? ['Months', selectedMonthIds.map((id) => allMonths.find((m) => m.month_id === id)?.month_name).filter(Boolean).sort(compareMonthNames).join(', ')]
                      : ['Quarters', selectedQuarterIds
                          .map((id) => allQuarters.find((q) => q.quarter_id === id)?.quarter_name)
                          .filter(Boolean).sort(compareQuarterNames).join(', ')],
                    ['Open Date',  openDate ? new Date(openDate).toLocaleString() : '—'],
                    ['Close Date', closeDate ? new Date(closeDate).toLocaleString() : '—'],
                  ].map(([k, v]) => (
                    <div key={k} className="review-row">
                      <span className="review-row__key">{k}</span>
                      <span className="review-row__val">{v || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="review-section">
                <h4>{isUniversal ? `🌐 Universal — All ${sectorLabel} Establishments` : `Establishments (${selectedEstIds.length})`}</h4>
                {isUniversal ? (
                  <div className="review-summary">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" style={{color:'var(--info)',flexShrink:0}}>
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                    </svg>
                    <span>
                      A <strong>Universal Survey</strong> targeting all active <strong>{sectorLabel}</strong> establishments will be created.{' '}
                      {autoDispatch
                        ? <span style={{color:'var(--success)',fontWeight:600}}>It will be automatically dispatched to all active providers in the sector.</span>
                        : <span>It will be saved as a <strong>draft</strong> — dispatch manually later.</span>
                      }
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="review-est-list">
                      {selectedEstIds.map((id) => {
                        const est = establishments.find((e) => e.establishment_id === id);
                        return est ? (
                          <div key={id} className="review-est-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12" style={{color:'var(--success)'}}>
                              <path fillRule="evenodd" d="M8 16A8 8 0 108 0a8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L7 8.586 5.707 7.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            <div>
                              <span className="review-est-item__name">{est.establishment_name}</span>
                              <span className="review-est-item__email">{est.establishment_email}</span>
                            </div>
                          </div>
                        ) : null;
                      })}
                    </div>
                    <div className="review-summary">
                      <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" style={{color:'var(--info)',flexShrink:0}}>
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                      </svg>
                      <span><strong>{selectedEstIds.length}</strong> survey(s) will be created in <strong>draft</strong> status.</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="create-survey">
      <PageHeader
        title="Create New Survey"
        subtitle="Set up a survey and assign it to establishments"
        actions={
          <Button variant="ghost" onClick={() => navigate('/internal/surveys')}>
            ← Back to Surveys
          </Button>
        }
      />

      {/* Step indicator */}
      <p className="step-indicator__caption">
        Step {step} of {STEPS.length} — {STEPS.find((s) => s.id === step)?.label}
      </p>
      <div className="step-indicator">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className={`step-dot ${step === s.id ? 'step-dot--active' : step > s.id ? 'step-dot--done' : ''}`}>
              {step > s.id ? (
                <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                  <path fillRule="evenodd" d="M13.707 4.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L6 10.586l6.293-6.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              ) : (
                <span>{s.icon}</span>
              )}
              <span className="step-dot__label">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`step-line ${step > s.id ? 'step-line--done' : ''}`} />}
          </React.Fragment>
        ))}
      </div>

      <Card className="create-survey__card">
        {renderStep()}
      </Card>

      <div className="create-survey__nav">
        <Button variant="secondary" disabled={step === 1} onClick={() => setStep((s) => s - 1)}>
          ← Previous
        </Button>
        <div className="create-survey__step-label">Step {step} of {STEPS.length}</div>
        {step < STEPS.length ? (
          <Button variant="primary" disabled={!canProceed()} onClick={() => setStep((s) => s + 1)}>
            Next →
          </Button>
        ) : (
          <Button variant="gold" loading={submitting} onClick={handleCreate}
            icon={<svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>}
          >
            {isUniversal ? 'Create Universal Survey' : `Create ${selectedEstIds.length} Survey${selectedEstIds.length !== 1 ? 's' : ''}`}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CreateSurvey;